
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { PasswordField } from "./shared/PasswordField";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { usePasswordValidation } from "@/hooks/auth/use-password-validation";

export const ChangePasswordForm = ({ onCancel }: { onCancel: () => void }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { passwordError, validatePasswordMatch, showPasswordError } = usePasswordValidation();

  const validateForm = () => {
    if (!currentPassword) {
      return showPasswordError("Current password is required");
    }

    return validatePasswordMatch(newPassword, confirmPassword);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // First, verify the current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: (await supabase.auth.getUser()).data.user?.email || "",
        password: currentPassword,
      });

      if (signInError) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Current password is incorrect",
        });
        setIsLoading(false);
        return;
      }

      // If sign-in is successful, update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        throw updateError;
      }

      toast({
        title: "Success",
        description: "Your password has been updated successfully",
      });
      
      onCancel(); // Close the form after success
    } catch (error: any) {
      console.error("Password change error:", error);
      
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "An error occurred while changing your password",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h3 className="text-xl font-semibold text-white">Change Password</h3>
        <p className="text-sm text-gray-300">
          Enter your current password and your new password below.
        </p>
      </div>
      
      <form onSubmit={handleChangePassword}>
        <div className="space-y-4">
          <PasswordField
            value={currentPassword}
            onChange={setCurrentPassword}
            placeholder="Current Password"
          />
          
          <PasswordField
            value={newPassword}
            onChange={setNewPassword}
            placeholder="New Password"
            minLength={6}
          />
          
          <PasswordField
            value={confirmPassword}
            onChange={setConfirmPassword}
            placeholder="Confirm New Password"
            minLength={6}
          />
          
          {passwordError && (
            <div className="text-red-400 text-sm">{passwordError}</div>
          )}
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              onClick={onCancel}
              variant="outline"
              className="bg-white/20 hover:bg-white/30 text-white border-white/30"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-gameGold text-gamePurple hover:bg-gameGold/90"
              disabled={isLoading}
            >
              {isLoading ? "Updating..." : "Update Password"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
